import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useCommissionerFeeStatus } from '@/hooks/useCommissionerFeeStatus';
import { useLocanceGeoCheck, type GeoBlockReason } from '@/hooks/useLocanceGeoCheck';
import { GeoBlockScreen } from '@/components/locance/GeoBlockScreen';
import { GEO_ENABLED } from '@/lib/locance';
import { acquireGeoSession } from '@/lib/acquireGeoSession';
import { mapDepositError } from '@/lib/depositErrors';
import { eligibilityToGeoBlock } from '@/lib/geoEligibility';
import {
  Users, Copy, Share2, Clock, UserPlus, Crown, Trash2, Play,
  CheckCircle2, Loader2, Mail, AlertTriangle, DollarSign,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface LobbyTeam {
  id: string;
  name: string;
  owner_id: string;
  owner_name: string;
  is_commissioner: boolean;
  joined_at: string;
}

interface NFLLeagueLobbyProps {
  onStartDraft?: () => void;
}

export default function NFLLeagueLobby({ onStartDraft }: NFLLeagueLobbyProps) {
  const { currentLeague, teams: contextTeams } = useNFLLeague();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  // SAB-591: commissioner entry-fee gate — same fail-closed geo posture as the
  // paid join (NFLJoinLeaguePage).
  const geo = useLocanceGeoCheck();
  const feeStatus = useCommissionerFeeStatus(currentLeague?.id);
  const [lobbyTeams, setLobbyTeams] = useState<LobbyTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [paying, setPaying] = useState(false);
  const [eligibilityBlock, setEligibilityBlock] = useState<GeoBlockReason | null>(null);

  const isCommissioner = currentLeague?.commissioner_id === user?.id;
  const inviteCode = (currentLeague as any)?.invite_code || '';
  /**
   * SAB-1000 (sibling audit): read the declared field, do not invent a capacity.
   *
   * Was `(currentLeague as any)?.max_teams || (currentLeague as any)?.team_count || 12`.
   * Three problems, in increasing order of severity:
   *
   *   - the `as any` casts are unnecessary; max_teams is declared on NFLLeague.
   *   - team_count is a DIFFERENT quantity (teams present, not capacity), so the first
   *     fallback silently answers a different question -- "3/3 teams joined, complete" for
   *     a league still waiting on nine more.
   *   - `|| 12` fabricates a capacity, and `||` also swallows a legitimate 0.
   *
   * Measured before changing it: max_teams is NEVER null and NEVER 0 on either lineage
   * (prod range 8..14, 10 of 22 leagues are not 12), so this fallback is dead code today,
   * not a live misstatement like the $1,500 banner. It is fixed as the latent hazard it is
   * -- the day max_teams goes null, a 14-team league would quietly report "of 12".
   */
  const maxTeams = currentLeague?.max_teams ?? null;
  // SAB-593: derive from the current origin, not hardcoded prod.
  const inviteLink = `${window.location.origin}/nfl/join/${inviteCode}`;

  const fetchTeams = useCallback(async () => {
    if (!currentLeague) return;

    const { data: teams } = await (supabase as any)
      .from('nfl_teams')
      .select('id, name, owner_id, created_at')
      .eq('league_id', currentLeague.id)
      // SAB-1108: a team that left is not in the league.
      .neq('status', 'left')
      .order('created_at');

    if (!teams) { setLoading(false); return; }

    // Fetch owner profiles
    const ownerIds = teams.map((t: any) => t.owner_id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name')
      .in('id', ownerIds);

    const profileMap: Record<string, string> = {};
    for (const p of profiles || []) profileMap[p.id] = p.display_name || 'Unknown';

    const enriched: LobbyTeam[] = teams.map((t: any) => ({
      id: t.id,
      name: t.name,
      owner_id: t.owner_id,
      owner_name: profileMap[t.owner_id] || 'Unknown',
      is_commissioner: t.owner_id === currentLeague.commissioner_id,
      joined_at: t.created_at,
    }));

    setLobbyTeams(enriched);
    setLoading(false);
  }, [currentLeague]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // Real-time subscription for team joins
  useEffect(() => {
    if (!currentLeague) return;

    const channel = supabase
      .channel(`nfl-lobby-${currentLeague.id}`)
      // SAB-846 follow-up. Explicit events, never '*' -- but note this lobby KEEPS DELETE,
      // which is the opposite call to the one made for nfl_draft_picks, and the difference
      // is the point.
      //
      // The earlier change narrowed nfl_draft_picks away from '*' specifically to shed
      // DELETE: RLS is not applied to DELETE events and they cannot be filtered, and that
      // handler only ever refetches, so a deletion told it nothing it needed. Here the
      // subscription drives a MEMBER LIST, and a team leaving or being removed by the
      // commissioner is exactly the change the list must reflect. Dropping DELETE would
      // leave departed teams on screen until reload.
      //
      // Safe because nfl_teams stays at REPLICA IDENTITY DEFAULT: the old record carries
      // the PRIMARY KEY ONLY, which is all fetchTeams() needs to be triggered, and is not
      // a disclosure. FULL would ship the whole row -- owner_id included -- unfiltered to
      // every channel subscriber, which is precisely what SAB-897 just closed.
      //
      // Listing all three explicitly is behaviourally identical to '*'. The value is that
      // DELETE is now a recorded decision rather than something '*' quietly included.
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'nfl_teams',
        filter: `league_id=eq.${currentLeague.id}`,
      }, () => { fetchTeams(); })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'nfl_teams',
        filter: `league_id=eq.${currentLeague.id}`,
      }, () => { fetchTeams(); })
      // DELETE carries NO FILTER, and that is not an oversight -- it is forced by the same
      // property that makes DELETE safe. Under REPLICA IDENTITY DEFAULT the old record is
      // the PRIMARY KEY ONLY, so `league_id=eq.<id>` has no league_id to match against and
      // the event is silently dropped.
      //
      // Measured on staging, not reasoned about: with the filter present, deleting a team
      // left the lobby showing 9 for 45s while the database held 8, and a reload showed 8.
      // A rename in the same league appeared in 18s, so realtime, the publication and this
      // channel were all fine -- it was the DELETE filter specifically.
      //
      // Unfiltered means this receives a bare uuid for every nfl_teams deletion anywhere,
      // including leagues the user is not in. That is not a disclosure: the payload is an
      // opaque id, and nfl_teams is member-scoped since SAB-897 so it cannot be resolved.
      // The cost is a spurious fetchTeams() when some other league loses a team, and
      // fetchTeams() is itself league-scoped, so the result stays correct.
      //
      // The alternative -- REPLICA IDENTITY FULL, so the filter has a league_id to match --
      // would ship the entire old row including owner_id, unfiltered and un-RLS'd, to every
      // subscriber. That is precisely what SAB-897 closed. Do not.
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'nfl_teams',
      }, () => { fetchTeams(); })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentLeague, fetchTeams]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      toast({ title: 'Copied!', description: 'Invite code copied' });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast({ title: 'Copied!', description: 'Invite link copied' });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const shareInvite = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${currentLeague?.name} on Sabotage Fantasy`,
          text: `Join my NFL league! Use code ${inviteCode}`,
          url: inviteLink,
        });
      } catch { /* cancelled */ }
    } else {
      copyLink();
    }
  };

  const handleRemoveTeam = async (teamId: string) => {
    if (!isCommissioner || !currentLeague) return;
    setRemoving(teamId);

    try {
      const team = lobbyTeams.find(t => t.id === teamId);
      if (!team) return;

      // Remove league member
      await (supabase as any)
        .from('nfl_league_members')
        .delete()
        .eq('league_id', currentLeague.id)
        .eq('user_id', team.owner_id);

      // Remove team
      await (supabase as any)
        .from('nfl_teams')
        .delete()
        .eq('id', teamId);

      toast({ title: 'Team removed' });
      fetchTeams();
    } catch (err) {
      toast({ title: 'Failed to remove', variant: 'destructive' });
    } finally {
      setRemoving(null);
    }
  };

  // SAB-591: the commissioner's entry fee runs the SAME money leg as a paid
  // member join (NFLJoinLeaguePage.handleJoin): declared-eligibility pre-check
  // (fail-open), Locance geo session (fail-closed under enforce), then POST
  // nfl-join-league with the league's own invite code. The server recognizes
  // the commissioner's existing seat and charges the fee (commissioner_paid).
  const handlePayEntryFee = async () => {
    if (!user || !currentLeague || !inviteCode || paying) return;
    setPaying(true);

    try {
      setEligibilityBlock(null);
      try {
        const { data: sess } = await supabase.auth.getSession();
        const eligRes = await fetch(
          `${(supabase as any).supabaseUrl}/functions/v1/check-eligibility`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${sess?.session?.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'get_user_status' }),
          }
        );
        const block = eligibilityToGeoBlock(await eligRes.json());
        if (block) {
          setEligibilityBlock(block);
          return;
        }
      } catch {
        // fail open — a down eligibility service must not block
      }

      // ok:false only under enforce-client on a non-Compliant verdict: abort
      // here and geo.geoBlock drives the GeoBlockScreen overlay below.
      const geoRes = await acquireGeoSession(geo, currentLeague.id);
      if (!geoRes.ok) {
        return;
      }

      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) {
        toast({ title: 'Not logged in', variant: 'destructive' });
        return;
      }

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-join-league`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invite_code: inviteCode,
            ...(geoRes.sessionId ? { geo_session_id: geoRes.sessionId } : {}),
          }),
        }
      );

      const data = await res.json();

      if (data.error) {
        if (data.error === 'insufficient_funds' || data.code === 'INSUFFICIENT_FUNDS') {
          // Deposit page prefilled with the shortfall — the join-page routing,
          // minus the pending-join stash: the commissioner already has a
          // membership, so the join page would dead-end in its already-member
          // state. They pay from this banner after funding.
          const available = typeof data.available === 'number' ? data.available : 0;
          const shortfall = Math.max(0, feeStatus.entryFee - available);
          const deposit = Math.max(shortfall, 10);
          toast({
            title: 'Add funds to pay your entry fee',
            description: data.message || `You need $${feeStatus.entryFee} in your wallet.`,
          });
          navigate(`/wallet/deposit?league=${currentLeague.id}&amount=${deposit}&shortfall=${shortfall}`);
        } else {
          const { title, message } = mapDepositError({ code: data.code, message: data.error, category: data.category, detected_state: data.detected_state });
          toast({ title, description: message, variant: 'destructive' });
        }
      } else {
        toast({ title: 'Entry fee paid!', description: `You're locked in — $${feeStatus.entryFee} added to the pot.` });
        feeStatus.refresh();
        fetchTeams();
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setPaying(false);
    }
  };

  if (!currentLeague) return null;

  const filledSpots = lobbyTeams.length;
  // maxTeams null (capacity unknown) must not read as a FULL league or a 100% bar:
  // `filledSpots >= null` is false in JS but `(n / null)` is Infinity, so the progress
  // bar would peg full while isFull stayed false -- two widgets disagreeing on screen.
  const emptySpots = maxTeams === null ? 0 : Math.max(0, maxTeams - filledSpots);
  const isFull = maxTeams !== null && filledSpots >= maxTeams;
  const progress = maxTeams === null || maxTeams === 0 ? 0 : (filledSpots / maxTeams) * 100;

  const showFeeBanner = feeStatus.required && !feeStatus.loading && !feeStatus.paid;

  return (
    <div className="space-y-4">
      {/* SAB-591: geo/eligibility block overlay for the pay flow — same layering
          as the paid join. Display-only; server gate is authoritative. */}
      {(eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)) && (
        <GeoBlockScreen
          reason={eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)}
          busy={geo.state === 'prompting' || geo.state === 'checking'}
          onRetry={() => handlePayEntryFee()}
          onDismiss={() => {
            setEligibilityBlock(null);
            geo.reset();
          }}
        />
      )}

      {/* SAB-591: unmissable commissioner pay state — the draft is server-locked
          (403 COMMISSIONER_UNPAID) until this fee is in. */}
      {showFeeBanner && (
        <Card id="commissioner-fee-banner" className="w-full bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-foreground">
                  Your seat is reserved — entry fee unpaid
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Pay your ${feeStatus.entryFee} entry fee to unlock the draft. The league cannot start until you do.
                </p>
              </div>
            </div>
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              onClick={handlePayEntryFee}
              disabled={paying}
            >
              {paying ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <DollarSign className="h-4 w-4 mr-2" />
              )}
              Pay entry fee (${feeStatus.entryFee})
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Invite card */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Invite Code</p>
            <div className="flex gap-1.5">
              <Button variant="ghost" size="sm" onClick={copyCode} className="h-7 px-2 text-[10px]">
                <Copy className="h-3 w-3 mr-1" /> Code
              </Button>
              <Button variant="ghost" size="sm" onClick={copyLink} className="h-7 px-2 text-[10px]">
                <Copy className="h-3 w-3 mr-1" /> Link
              </Button>
              <Button variant="ghost" size="sm" onClick={shareInvite} className="h-7 px-2 text-[10px]">
                <Share2 className="h-3 w-3 mr-1" /> Share
              </Button>
            </div>
          </div>
          <p className="text-3xl font-mono font-bold text-primary tracking-[0.3em] text-center">
            {inviteCode}
          </p>
          <p className="text-[10px] text-muted-foreground text-center">
            {inviteLink}
          </p>

          {/* Email invite */}
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="friend@email.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="text-xs h-8"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={sendingEmail || !inviteEmail.trim()}
              className="h-8 px-3 text-[10px] shrink-0"
              onClick={async () => {
                if (!inviteEmail.trim() || !currentLeague) return;
                setSendingEmail(true);
                try {
                  const { data: { session } } = await supabase.auth.getSession();
                  const res = await fetch(
                    // SAB-852: was `import.meta.env.VITE_SUPABASE_URL || '<a hardcoded host>'`, where
                    // that host was a DEAD project -- NXDOMAIN from every public resolver, and not
                    // one of the two projects in the org. It never fired in production (Vite folds
                    // the env in at build time), but it was a second source of truth with its own
                    // fallback, and `||` rather than `??`, so it swallowed explicit-empty too.
                    // The ref is deliberately not repeated here: leaving it in prose would keep it
                    // answering greps forever, which is the trap that made it look live today.
                    // Now uses the client's own resolved URL, matching the two sibling calls in
                    // this same file (check-eligibility, nfl-join-league) and the convention
                    // everywhere else in the app.
                    `${(supabase as any).supabaseUrl}/functions/v1/nfl-send-invite-email`,
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session?.access_token}`,
                      },
                      body: JSON.stringify({
                        league_id: currentLeague.id,
                        emails: inviteEmail.split(',').map(e => e.trim()).filter(Boolean),
                      }),
                    }
                  );
                  const result = await res.json();
                  // SAB-686: branch THREE ways, not two. The old code showed a success
                  // toast whenever `sent > 0`, so inviting 12 and reaching 3 rendered
                  // "Invite sent! 3 email(s) sent" and never mentioned the other 9 --
                  // a false confirmation at the acquisition moment.
                  const invalid: string[] = result.invalid_addresses ?? [];
                  const failed: string[] = result.failed_addresses ?? [];
                  const sentCount: number = result.sent ?? 0;
                  const problems = invalid.length + failed.length;

                  if (sentCount > 0 && problems === 0) {
                    // all sent
                    toast({ title: 'Invites sent', description: `${sentCount} email(s) sent` });
                    setInviteEmail('');
                  } else if (sentCount > 0) {
                    // PARTIAL -- never a success toast. Name the addresses so the
                    // commissioner can retype a typo or retry a refused send, and leave
                    // the input populated with exactly those so they do not have to.
                    const parts: string[] = [];
                    if (invalid.length) parts.push(`${invalid.length} looked invalid: ${invalid.join(', ')}`);
                    if (failed.length) parts.push(`${failed.length} could not be delivered: ${failed.join(', ')}`);
                    toast({
                      title: `Sent ${sentCount}, but ${problems} did not go out`,
                      description: parts.join(' · '),
                      variant: 'destructive',
                    });
                    setInviteEmail([...invalid, ...failed].join(', '));
                  } else {
                    const detail = invalid.length && !failed.length
                      ? 'Check the addresses and try again.'
                      : 'Nothing was sent. Try again in a moment.';
                    toast({ title: 'No invites sent', description: detail, variant: 'destructive' });
                  }
                } catch {
                  toast({ title: 'Failed to send', variant: 'destructive' });
                } finally {
                  setSendingEmail(false);
                }
              }}
            >
              <Mail className="h-3 w-3 mr-1" />
              {sendingEmail ? 'Sending...' : 'Invite'}
            </Button>
          </div>

          {/* Pre-formatted share text */}
          <div className="p-2.5 rounded-lg bg-muted/30 border border-border">
            <p className="text-[10px] text-muted-foreground italic">
              "Join my Sabotage Fantasy NFL league! Use code: <span className="text-foreground font-bold">{inviteCode}</span> or click: {inviteLink}"
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Progress */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-bold text-foreground">{maxTeams === null ? `${filledSpots} Teams` : `${filledSpots}/${maxTeams} Teams`}</span>
            </div>
            {isFull ? (
              <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Full
              </Badge>
            ) : (
              <span className="text-[10px] text-muted-foreground">{emptySpots} spot{emptySpots !== 1 ? 's' : ''} remaining</span>
            )}
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                isFull ? 'bg-green-500' : 'bg-primary'
              )}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Team list */}
          <div className="divide-y divide-border/30">
            {lobbyTeams.map((team, i) => (
              <div key={team.id} className="flex items-center gap-3 py-2.5">
                <div className={cn(
                  'h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                  team.is_commissioner ? 'bg-primary/10 text-primary' : 'bg-muted/50 text-foreground'
                )}>
                  {i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-foreground truncate">{team.name}</p>
                    {team.is_commissioner && (
                      <Crown className="h-3 w-3 text-primary shrink-0" />
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground">{team.owner_name}</p>
                </div>
                {isCommissioner && !team.is_commissioner && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveTeam(team.id)}
                    disabled={removing === team.id}
                    className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    {removing === team.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            ))}

            {/* Empty slots */}
            {Array.from({ length: emptySpots }).map((_, i) => (
              <div key={`empty-${i}`} className="flex items-center gap-3 py-2.5">
                <div className="h-7 w-7 rounded-full bg-muted/20 flex items-center justify-center shrink-0">
                  <UserPlus className="h-3 w-3 text-muted-foreground/40" />
                </div>
                <p className="text-xs text-muted-foreground/40 italic">Waiting for player...</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Start draft button (commissioner only, when full or any time) */}
      {isCommissioner && filledSpots >= 2 && (
        <Card className={cn(
          'border overflow-hidden',
          isFull
            ? 'bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30'
            : 'bg-card border-border'
        )}>
          <CardContent className="p-4 text-center space-y-2">
            {isFull ? (
              <p className="text-sm font-semibold text-foreground">All teams joined! Ready to draft.</p>
            ) : (
              <p className="text-xs text-muted-foreground">You can start the draft with {filledSpots} teams or wait for more.</p>
            )}
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={onStartDraft}
            >
              <Play className="h-4 w-4 mr-2" />
              Start Draft
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
